// Mitmach-Module: Jedes Radio schaltet selbst ein, was es nutzen möchte.
//
// Ein Modul bündelt alles, was zu einer Mitmach-Funktion gehört: die Blöcke auf der
// Hörerseite, die Seiten in der Suite, die Endpunkte der API und das, was im Studio
// dazu erscheint. Ist es aus, gibt es nichts davon – keine Blöcke in der Auswahl,
// keine Eingaben von Hörern, kein Panel im Studio.
//
// Neues Modul ergänzen:
//   1. Hier eintragen (Kennung, Name, Erklärung, Blöcke, Einstellungen).
//   2. In der API die Endpunkte mit `requireModule("<kennung>")` absichern.
//   3. Blöcke wie gewohnt im Baukasten anlegen; die Zuordnung übernimmt `blocks`.
import { z } from "zod";
/** Wie oft ein Gerät mitmachen darf – bei allen Abstimmungen gleich aufgebaut. */
const perDevice = (label, max) => ({
    field: { name: "per_device", label, type: "number", min: 1, max: 100, hint: "Schutz davor, dass einer allein das Ergebnis macht." },
    schema: z.number().int().min(1).max(100).default(max),
    value: max,
});
const votes = perDevice("Stimmen je Gerät und Abstimmung", 1);
const quizTries = perDevice("Versuche je Gerät und Frage", 1);
const ratings = perDevice("Bewertungen je Gerät und Titel", 1);
const MODULE_DEFS = {
    poll: {
        label: "Live-Abstimmung",
        hint: "Der Moderator stellt eine Frage, Hörer stimmen ab, das Ergebnis wächst live mit.",
        blocks: ["poll"],
        studio: true,
        fields: [
            votes.field,
            { name: "show_results", label: "Ergebnis zeigen", type: "select", options: [
                    { value: "immer", label: "immer" }, { value: "nach_stimme", label: "erst nach eigener Stimme" }, { value: "am_ende", label: "erst am Ende" },
                ] },
            { name: "winner_to_queue", label: "Gewinner in die Queue anbieten", type: "boolean", hint: "Im Studio erscheint ein Knopf, der den Gewinnertitel einreiht." },
        ],
        schema: z.object({
            per_device: votes.schema,
            show_results: z.enum(["immer", "nach_stimme", "am_ende"]).default("nach_stimme"),
            winner_to_queue: z.boolean().default(true),
        }),
        defaults: { per_device: votes.value, show_results: "nach_stimme", winner_to_queue: true },
    },
    charts: {
        label: "Hörer-Charts",
        hint: "Aus Wünschen und Bewertungen entsteht eine Top-Liste, die ihr als Sendung spielen könnt.",
        blocks: ["charts"],
        fields: [
            { name: "size", label: "Wie viele Titel", type: "number", min: 3, max: 50 },
            { name: "period_days", label: "Zeitraum in Tagen", type: "number", min: 1, max: 90 },
            { name: "playlist", label: "Playlist daraus pflegen", type: "boolean", hint: "Legt eine Playlist an und hält sie aktuell." },
        ],
        schema: z.object({
            size: z.number().int().min(3).max(50).default(10),
            period_days: z.number().int().min(1).max(90).default(7),
            playlist: z.boolean().default(true),
        }),
        defaults: { size: 10, period_days: 7, playlist: true },
    },
    greetings: {
        label: "Grußwand",
        hint: "Freigegebene Grüße und Nachrichten erscheinen auf der Hörerseite.",
        blocks: ["greetings"],
        studio: true,
        fields: [
            { name: "keep_days", label: "Grüße stehen lassen (Tage)", type: "number", min: 1, max: 30 },
            { name: "auto", label: "Ohne Freigabe zeigen", type: "boolean", hint: "Nicht empfohlen: Dann steht jeder Text sofort öffentlich." },
        ],
        schema: z.object({ keep_days: z.number().int().min(1).max(30).default(7), auto: z.boolean().default(false) }),
        defaults: { keep_days: 7, auto: false },
    },
    chat: {
        label: "Hörerchat",
        hint: "Ein Raum je Kanal, in dem Hörer und Moderation während der Sendung miteinander schreiben.",
        blocks: ["chat"],
        studio: true,
        fields: [
            { name: "guests", label: "Gäste dürfen mitschreiben", type: "boolean", hint: "Aus: Gäste lesen mit, schreiben dürfen nur Team-Zugänge." },
            { name: "only_on_air", label: "Nur während einer Live-Sendung", type: "boolean", hint: "An: Der Raum ist zu, solange niemand moderiert – wie beim Hörertelefon." },
            { name: "slow_seconds", label: "Abstand zwischen zwei Zeilen (Sekunden)", type: "number", min: 0, max: 60, hint: "Bremst Dauerschreiber. Für das Team gilt er nicht." },
            { name: "keep_hours", label: "Verlauf aufbewahren (Stunden)", type: "number", min: 1, max: 720, hint: "Danach wird gelöscht. 24 heißt: Am nächsten Tag ist der Raum leer." },
            { name: "welcome", label: "Begrüßung im Raum", type: "textarea", hint: "Steht ganz oben, bevor die erste Zeile kommt." },
            { name: "rules", label: "Regeln", type: "markdown", hint: "Werden beim Eintreten gezeigt und müssen bestätigt werden. Leer: nur der Datenschutzhinweis." },
        ],
        schema: z.object({
            guests: z.boolean().default(true),
            only_on_air: z.boolean().default(false),
            slow_seconds: z.number().int().min(0).max(60).default(3),
            keep_hours: z.number().int().min(1).max(720).default(24),
            welcome: z.string().trim().max(300).default("Schön, dass du da bist. Bleib freundlich – hier lesen alle mit."),
            rules: z.string().trim().max(4000).default(""),
        }),
        defaults: {
            guests: true, only_on_air: false, slow_seconds: 3, keep_hours: 24,
            welcome: "Schön, dass du da bist. Bleib freundlich – hier lesen alle mit.", rules: "",
        },
    },
    phone: {
        label: "Hörertelefon",
        hint: "Hörer melden sich über den Browser, warten in der Schlange und kommen auf Sendung.",
        blocks: ["phone"],
        studio: true,
        fields: [
            { name: "queue_max", label: "Wie viele warten dürfen", type: "number", min: 1, max: 20 },
            { name: "ask_place", label: "Nach dem Ort fragen", type: "boolean" },
            { name: "note", label: "Hinweis für Anrufer", type: "textarea", hint: "Steht über dem Knopf, z. B. „Bitte Kopfhörer benutzen“." },
        ],
        schema: z.object({
            queue_max: z.number().int().min(1).max(20).default(5),
            ask_place: z.boolean().default(true),
            note: z.string().trim().max(300).default("Bitte Kopfhörer benutzen, sonst gibt es ein Echo."),
        }),
        defaults: { queue_max: 5, ask_place: true, note: "Bitte Kopfhörer benutzen, sonst gibt es ein Echo." },
    },
    ratings: {
        label: "Titel bewerten",
        hint: "Daumen hoch oder runter am laufenden Titel; die Auswertung zeigt, was ankommt.",
        blocks: ["rating"],
        fields: [
            ratings.field,
            { name: "affect_rotation", label: "In die Rotation einfließen lassen", type: "boolean", hint: "Schlecht bewertete Titel kommen seltener." },
            { name: "min_votes", label: "Erst ab so vielen Stimmen werten", type: "number", min: 1, max: 500 },
        ],
        schema: z.object({
            per_device: ratings.schema,
            affect_rotation: z.boolean().default(false),
            min_votes: z.number().int().min(1).max(500).default(20),
        }),
        defaults: { per_device: ratings.value, affect_rotation: false, min_votes: 20 },
    },
    contest: {
        label: "Gewinnspiel",
        hint: "Frage stellen, Hörer machen mit, die Suite zieht den Gewinner.",
        blocks: ["contest"],
        studio: true,
        fields: [
            { name: "entries_per_device", label: "Teilnahmen je Gerät", type: "number", min: 1, max: 10 },
            { name: "keep_days", label: "Teilnahmen aufbewahren (Tage)", type: "number", min: 1, max: 90, hint: "Danach werden Name und Kontakt gelöscht." },
            { name: "terms", label: "Teilnahmebedingungen", type: "markdown", hint: "Leer: Vorlage der Suite, ergänzt um euren Namen." },
        ],
        schema: z.object({
            entries_per_device: z.number().int().min(1).max(10).default(1),
            keep_days: z.number().int().min(1).max(90).default(30),
            terms: z.string().trim().max(8000).default(""),
        }),
        defaults: { entries_per_device: 1, keep_days: 30, terms: "" },
    },
    reminders: {
        label: "Erinnerungen",
        hint: "Hörer lassen sich benachrichtigen, wenn eine Sendung beginnt oder ihr Wunsch läuft – ohne App und ohne Konto.",
        blocks: ["reminders"],
        fields: [
            { name: "shows", label: "Für Sendungen anbieten", type: "boolean" },
            { name: "requests", label: "Für eigene Wünsche anbieten", type: "boolean" },
            { name: "lead_minutes", label: "Vorlauf in Minuten", type: "number", min: 0, max: 120 },
        ],
        schema: z.object({
            shows: z.boolean().default(true),
            requests: z.boolean().default(true),
            lead_minutes: z.number().int().min(0).max(120).default(10),
        }),
        defaults: { shows: true, requests: true, lead_minutes: 10 },
    },
    events: {
        label: "Mitmach-Kalender",
        hint: "Hörer reichen Veranstaltungen ein, die Redaktion gibt frei.",
        blocks: ["events", "event_form"],
        studio: true,
        fields: [
            { name: "region", label: "Hinweis zur Region", type: "text", hint: "Steht über dem Formular, z. B. „Termine aus der Eifel“." },
            { name: "days_ahead", label: "Wie weit voraus zeigen (Tage)", type: "number", min: 7, max: 365 },
        ],
        schema: z.object({ region: z.string().trim().max(120).default(""), days_ahead: z.number().int().min(7).max(365).default(60) }),
        defaults: { region: "", days_ahead: 60 },
    },
    voice_survey: {
        label: "Frage der Woche",
        hint: "Hörer antworten per Sprachnachricht; daraus schneidet ihr einen O-Ton-Block.",
        blocks: ["voice_survey"],
        studio: true,
        fields: [
            { name: "max_seconds", label: "Länge je Antwort (Sekunden)", type: "number", min: 10, max: 120 },
            { name: "public_answers", label: "Antworten öffentlich zeigen", type: "boolean", hint: "Nur freigegebene, und nur mit Vornamen." },
        ],
        schema: z.object({ max_seconds: z.number().int().min(10).max(120).default(45), public_answers: z.boolean().default(false) }),
        defaults: { max_seconds: 45, public_answers: false },
    },
    quiz: {
        label: "Mitraten",
        hint: "Quizfrage in der Sendung, Hörer tippen mit, der Zähler läuft live.",
        blocks: ["quiz"],
        studio: true,
        fields: [
            quizTries.field,
            { name: "show_answer", label: "Auflösung zeigen", type: "select", options: [
                    { value: "sofort", label: "sofort nach dem Tipp" }, { value: "am_ende", label: "erst bei der Auflösung" },
                ] },
            { name: "scoreboard", label: "Bestenliste führen", type: "boolean", hint: "Zählt richtige Antworten je Spitzname über eine Sendung." },
        ],
        schema: z.object({
            per_device: quizTries.schema,
            show_answer: z.enum(["sofort", "am_ende"]).default("am_ende"),
            scoreboard: z.boolean().default(true),
        }),
        defaults: { per_device: quizTries.value, show_answer: "am_ende", scoreboard: true },
    },
    // Ans Ende gehängt, damit parallele Zweige sich hier nicht in die Quere kommen.
    //
    // Nicht verwechseln mit „events“ darüber: Das ist der öffentliche Mitmach-Kalender.
    // Hier geht es um eine gebuchte Feier, die nur die geladenen Gäste hören.
    veranstaltung: {
        label: "Private Veranstaltungen",
        hint: "Jemand bucht einen Termin – Hochzeit, Geburtstag, Vereinsfest – und nur seine Gäste hören mit.",
        blocks: ["veranstaltung_form"],
        fields: [
            { name: "channel", label: "Kanal für die Feiern", type: "channel", hint: "Ein eigener, nicht öffentlich gelisteter Kanal. Das laufende Programm bleibt davon unberührt." },
            { name: "intro", label: "Hinweis über dem Formular", type: "textarea", hint: "Zum Beispiel, was ihr anbietet und was es kostet. Gezahlt wird außerhalb der Suite." },
            { name: "min_lead_days", label: "Vorlauf für Anfragen (Tage)", type: "number", min: 0, max: 90 },
            { name: "max_hours", label: "Längste Feier (Stunden)", type: "number", min: 1, max: 24 },
            { name: "lead_minutes", label: "Ton schon vorher freigeben (Minuten)", type: "number", min: 0, max: 240 },
            { name: "trail_minutes", label: "Ton noch nachlaufen lassen (Minuten)", type: "number", min: 0, max: 240 },
            { name: "device_limit", label: "Geräte je Einladung", type: "number", min: 1, max: 20, hint: "Hält den weitergereichten Link klein. Wer ein Mikrofon danebenhält, lässt sich damit nicht aufhalten." },
            { name: "gift_days", label: "Mitschnitt für den Gastgeber (Tage)", type: "number", min: 0, max: 60, hint: "0: kein Mitschnitt zum Herunterladen." },
        ],
        schema: z.object({
            channel: z.string().trim().max(64).default(""),
            intro: z.string().trim().max(600).default(""),
            min_lead_days: z.number().int().min(0).max(90).default(7),
            max_hours: z.number().int().min(1).max(24).default(12),
            lead_minutes: z.number().int().min(0).max(240).default(30),
            trail_minutes: z.number().int().min(0).max(240).default(30),
            device_limit: z.number().int().min(1).max(20).default(2),
            gift_days: z.number().int().min(0).max(60).default(14),
        }),
        defaults: {
            channel: "", intro: "", min_lead_days: 7, max_hours: 12,
            lead_minutes: 30, trail_minutes: 30, device_limit: 2, gift_days: 14,
        },
    },
};
export const MODULE_IDS = Object.keys(MODULE_DEFS);
export const MODULES = Object.fromEntries(Object.entries(MODULE_DEFS).map(([id, def]) => [id, { ...def, id }]));
export function isModuleId(value) {
    return typeof value === "string" && Object.prototype.hasOwnProperty.call(MODULE_DEFS, value);
}
/** Zu welchem Modul gehört eine Blockart – oder `null`, wenn sie immer verfügbar ist. */
export function moduleForBlock(kind) {
    for (const id of MODULE_IDS) {
        if (MODULES[id].blocks.includes(kind))
            return id;
    }
    return null;
}
/** Darf dieser Block gezeichnet werden? Blöcke ohne Modul immer, sonst nur bei eingeschaltetem Modul. */
export function blockAllowed(kind, enabled) {
    const id = moduleForBlock(kind);
    if (!id)
        return true;
    for (const e of enabled) {
        if (e === id)
            return true;
    }
    return false;
}
/** Einstellungen prüfen; was fehlt oder nicht passt, wird durch den Standard ersetzt. */
export function parseModuleSettings(id, value) {
    const parsed = MODULES[id].schema.safeParse(value ?? {});
    return parsed.success ? parsed.data : { ...MODULES[id].defaults };
}
